import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import HomeIcon from '@mui/icons-material/Home';
import { tA11y } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { useAlert } from '@lwtears/lwt-common-frontend/lib/Alert';
import { remCalc } from '@lwtears/lwt-common-frontend/lib/@common/util/style-util';
import CloseButton from '../common/Buttons/CloseButton';

const SectionHeader: React.FC<{
  backRoute?: string;
  children?: React.ReactNode;
  closeButtonAction?: any;
  hamburgerMenu?: boolean;
  showCloseButton?: boolean;
  showDashboardIcon?: boolean;
  title?: string;
}> = ({
  backRoute,
  children,
  closeButtonAction,
  hamburgerMenu = true,
  showCloseButton,
  showDashboardIcon = true,
  title,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [, { closeAlert }] = useAlert();

  const toggle = () => setOpen(!open);

  const navigate = useNavigate();
  const location = useLocation();

  const isDashboard = location.pathname === '/home';
  const backNavigationRoute = backRoute || '/home';

  const backNavigationAction = () => {
    closeAlert();
    navigate(backNavigationRoute, { replace: true });
  };

  const icon = backNavigationRoute === '/home' ? faHome : faArrowLeft;

  return (
    <div className={classes.root}>
      <AppBar position="static" className={classes.appBarRoot}>
        <Toolbar classes={{ root: classes.customToolbar }}>
          <div className={classes.leftIcons}>
            {hamburgerMenu && (
              <>
                <IconButton
                  onClick={toggle}
                  edge="start"
                  className={classes.menuButton}
                  color="inherit"
                  aria-label={tA11y('buttons.menu')}
                  size="large"
                >
                  <HomeIcon />
                </IconButton>
                <Divider orientation="vertical" flexItem />
              </>
            )}
            {!isDashboard && showDashboardIcon && (
              <>
                <IconButton
                  onClick={backNavigationAction}
                  edge="start"
                  className={classes.homeButton}
                  color="inherit"
                  aria-label={tA11y('buttons.menu')}
                  size="large"
                >
                  <FontAwesomeIcon icon={icon} />
                </IconButton>
                <Divider orientation="vertical" flexItem />
              </>
            )}
            <Typography variant="h4" className={classes.title}>
              {title ?? ''}
            </Typography>
          </div>
          <div className={classes.rightIcons}>
            {children}
            {showCloseButton && (
              <>
                <Divider orientation="vertical" />
                <CloseButton onClick={closeButtonAction} color={'white'} margin={'0 -12px 0 0'} />
              </>
            )}
          </div>
        </Toolbar>
      </AppBar>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    top: 0,
    flexGrow: 1,
    position: 'sticky',
    zIndex: 99,
  },
  customToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: remCalc(theme.constants.app.menuOffset),
  },
  leftIcons: {
    display: 'inline-flex',
    alignItems: 'center',
    height: remCalc(theme.constants.app.menuOffset),
  },
  rightIcons: {
    display: 'inline-flex',
    alignItems: 'center',
    height: remCalc(theme.constants.app.menuOffset),
  },
  appBarRoot: {
    backgroundColor: `${theme.palette.secondary.dark} !important`,
    color: 'white !important',
  },
  menuButton: {
    paddingRight: 20,
    paddingLeft: 20,
  },
  homeButton: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  title: {
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.4rem',
    },
  },
  closeButtonDiv: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    margin: theme.spacing(3),
  },
  closeButton: {
    paddingLeft: 15,
    [theme.breakpoints.down('lg')]: {
      paddingLeft: 20,
    },
  },
}));

export default SectionHeader;
